import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { RobotoLinkHoverUnderline } from "@/shared/components";
import { DeviceLink } from "@/shared/components/DeviceLink";
import {
  ResourceView,
  activeFiltersToQuerySubmission,
  renderPrincipal,
  useResourceView,
  type FilterDefinition,
  type RoboqlMenuConfig,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { DatasetRecord } from "@/shared/domain/datasets/DatasetRecord";
import {
  datasetQueryKeys,
  useDatasetMetadataKeys,
} from "@/shared/domain/datasets/hooks";
import {
  composeQueryBody,
  computeMetadataRows,
  formatMetadataValue,
  projectRows,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
  type MetadataKey,
  type Row,
} from "@/shared/domain/query";
import {
  useMetadataValues,
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useDatasetTablePreferences } from "@/shared/domain/users/hooks";
import {
  ColumnVisibility,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  cellStyles,
  colId,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation/Goto";
import type { Condition, SearchQueryBody } from "@/types";

import styles from "./DatasetsList.module.css";

const ROBOQL_CONFIG: RoboqlMenuConfig = { target: QueryTarget.Datasets };

export type DatasetRow = Row<DatasetRecord>;

const getDatasetId: EntityIdGetter<DatasetRecord> = (d) => d.dataset_id;

const columnHelper = createColumnHelper<DatasetRow>();

const STATIC_DATASET_COLUMNS: ColDef<DatasetRow>[] = [
  columnHelper.accessor("dataset_id", {
    headerName: "ID",
    minWidth: 150,
    cellRenderer: (p: ICellRendererParams<DatasetRow, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Datasets}/${id}`}
          className={classNames(cellStyles.idCell)}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: true,
    cellRenderer: (p: ICellRendererParams<DatasetRow, string>) => {
      const name = p.value;
      if (!name) {
        return <span className={cellStyles.placeholder}>Untitled</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={`${Goto.route.Datasets}/${p.data?.dataset_id}`}
          className={classNames(cellStyles.primaryContent)}
        >
          {name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 120,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    minWidth: 120,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org",
    minWidth: 150,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<DatasetRow, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
  columnHelper.accessor("device_id", {
    headerName: "Device",
    minWidth: 150,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("storage_location", {
    headerName: "Storage Location",
    minWidth: 140,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("administrator", {
    headerName: "Administrator",
    minWidth: 140,
    cellRenderer: renderDashOrValue,
  }),
  {
    colId: "source",
    headerName: "Source",
    minWidth: 150,
    valueGetter: (p: { data?: DatasetRow }) =>
      p.data?.device_id ?? p.data?.created_by ?? "",
    cellRenderer: SourceCell,
  },
];

function buildMetadataColumn(metadataKey: string): ColDef<DatasetRow> {
  const id: MetadataKey = `metadata.${metadataKey}`;
  return {
    colId: id,
    headerName: metadataKey,
    // Floor so a flex metadata column doesn't collapse when the grid overflows.
    minWidth: 120,
    valueGetter: (p) => p.data?.[id],
    valueFormatter: (p) => formatMetadataValue(p.value),
    cellRenderer: renderDashOrValue,
    // Dataset metadata is never sortable
    sortable: false,
  };
}

const DEFAULT_VISIBLE_IDS = [
  "dataset_id",
  "name",
  "created",
  "created_by",
  "modified",
  "modified_by",
  "org_id",
  "tags",
];

const STATIC_PICKER_OPTIONS: ColumnPickerOption[] = STATIC_DATASET_COLUMNS.map(
  (c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }),
);

interface DatasetsListProps {
  /** Parent-provided scoping conditions (e.g. a collection filter).
   *  ANDed with any user-driven table filters */
  conditions?: Condition[];
  /** Bulk-action options surfaced in the DataTable.Selection menu. */
  selectionOptions?: SelectionOption<DatasetRow>[];
  /** Right-aligned toolbar content, e.g. a create button. */
  actions?: React.ReactNode;
}

export function DatasetsList({
  conditions,
  selectionOptions,
  actions,
}: DatasetsListProps = {}) {
  const { currentOrganization } = useAuth();
  const { datasetService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useDatasetTablePreferences();

  const metadataKeysQuery = useDatasetMetadataKeys(resolvedOrgId);

  // Capture initial columns preferences once at mount.
  const [initialColumnIds] = React.useState(() => preferences.columns);

  const customFields = useCustomFieldGridIntegration<DatasetRow>({
    entityType: TargetEntityType.Dataset,
    preferenceOrder: initialColumnIds,
    savePreferences,
  });

  // Custom-field columns come before metadata columns (after the static set).
  const columns = React.useMemo<ColDef<DatasetRow>[]>(
    () =>
      buildColumnsForPreferences<DatasetRow>({
        staticColumns: STATIC_DATASET_COLUMNS,
        dynamicColumns: [
          ...customFields.customFieldColumns,
          ...(metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        ],
        preferenceOrder: customFields.effectivePreferenceOrder,
      }),
    [
      customFields.customFieldColumns,
      customFields.effectivePreferenceOrder,
      metadataKeysQuery.data,
    ],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_PICKER_OPTIONS,
      ...customFields.columnPickerOptions,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [customFields.columnPickerOptions, metadataKeysQuery.data],
  );

  const view = useResourceView<DatasetRow>({
    getRowId: getDatasetId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "dataset_id", name: "Dataset Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      {
        type: "set",
        field: "tags",
        name: "Tags",
        options: function getDatasetsTags() {
          return datasetService.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
      ...customFields.filterDefinitions,
    ],
    [customFields.filterDefinitions, datasetService, resolvedOrgId],
  );

  const submissionState = activeFiltersToQuerySubmission({
    filters: view.filters,
    sort: view.table.sort,
  });

  let scopeCondition: Condition | undefined;
  if (conditions && conditions.length === 1) {
    scopeCondition = conditions[0];
  } else if (conditions && conditions.length > 1) {
    scopeCondition = { operator: "AND" as const, conditions };
  }
  const body: SearchQueryBody = composeQueryBody({
    scopeCondition,
    filterCondition: submissionState.filterCondition,
    sort: submissionState.sort,
    maxResults: submissionState.maxResults,
  });

  const submission = useStructuredQuery({
    resolvedOrgId,
    query: body,
    target: QueryTarget.Datasets,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: datasetQueryKeys.list(resolvedOrgId, body),
  });

  const queryId = submission.data?.query_id;

  const pageQuery = usePaginatedQueryResults<DatasetRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const datasets: DatasetRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const visibleMetadataKeys = React.useMemo<MetadataKey[]>(() => {
    const out: MetadataKey[] = [];
    for (const id of view.table.visibleColumnIds) {
      if (id.startsWith("metadata.")) {
        out.push(id as MetadataKey);
      }
    }
    return out;
  }, [view.table.visibleColumnIds]);

  const { getMetadataValue } = useMetadataValues({
    target: QueryTarget.Datasets,
    metadataColumns: visibleMetadataKeys,
    entityIds: datasets.map(getDatasetId),
  });

  const metadataByDataset = React.useMemo(
    () =>
      computeMetadataRows(
        datasets,
        visibleMetadataKeys,
        getMetadataValue,
        getDatasetId,
      ),
    [datasets, visibleMetadataKeys, getMetadataValue],
  );

  const rows = React.useMemo<DatasetRow[]>(
    () => projectRows(datasets, metadataByDataset, getDatasetId),
    [datasets, metadataByDataset],
  );

  let loading: DataTableLoadingState = "idle";
  if (submission.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submission.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  // The top-of-page Alert surfaces non-parse failures (network, timeout,
  // refetch errors). Parse errors from the roboql mutation surface inline
  // in `<RoboqlFilterMenu>` via the menu's submit handle.
  const fetchError = submission.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={filterDefinitions}
            filters={view.filters}
            onFilterSelected={view.commit}
            roboql={ROBOQL_CONFIG}
          />
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          {hasSelectionOptions && (
            <ResourceView.Selection<DatasetRow>
              selection={view.table.selection}
              clearSelection={view.table.clearSelection}
              options={selectionOptions ?? []}
            />
          )}
          {actions && <div className={styles.actionsRight}>{actions}</div>}
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={filterDefinitions}
          onRemove={view.removeFilter}
          onUpdate={view.updateFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
          roboql={ROBOQL_CONFIG}
        />
        <DataTable<DatasetRow>
          defaultColDef={FLEX_FILL_COL_DEF}
          table={view.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No datasets"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
        />
      </ResourceView.Root>
    </>
  );
}

function SourceCell(params: ICellRendererParams<DatasetRow, string>) {
  const row = params.data;
  if (row?.device_id) {
    return (
      <DeviceLink
        deviceId={row.device_id}
        linkSx={{ fontSize: "var(--font-size-sm)" }}
      />
    );
  }
  return renderPrincipal({ ...params, value: row?.created_by ?? "" });
}
